import React from 'react';
import { Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faCheckCircle,
    faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexCenterRow,
    FormDivider,
    FormGroupColumn,
    GroupedRow,
    ToastMessage,
    Input,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { UserGroup } from './UserGroup';

class UserGroupForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            group: new UserGroup(),
            userGroups: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { group } = this.state;
        const response = await httpClient
            .delete(ApiRoutes.userGroup.delete(group.id))
            .catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onSubmit = async (group) => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const cloned = clone(group);

        if (this.props.location.pathname == AppNavPaths.UserGroupNew) {
            await this.createGroup(cloned);
        } else {
            await this.updateGroup(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    createGroup = async (group) => {
        const response = await httpClient
            .post(ApiRoutes.userGroup.create(), group)
            .catch(this.handleSaveError);

        if (response && !isNaN(response.data)) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );

            this.context.history.push(
                `${AppNavPaths.UserGroup}/${response.data}`,
            );
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const groupId = this.props.match.params.id;

        const [userGroups, group] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.employeeGroups()),
            groupId
                ? httpClient.js(ApiRoutes.userGroup.byId(groupId))
                : new UserGroup(),
        ]);

        const originalData = clone(group);

        this.setState({
            originalData,
            group,
            loading: false,
            userGroups,
            saving: false,
        });
    }

    resetForm = () => this.setState({ formValidated: false });

    updateGroup = async (group) => {
        const response = await httpClient
            .put(ApiRoutes.userGroup.byId(group.id), group)
            .catch(this.handleSaveError);

        if (response && parseInt(response.data.Id ?? 0) >= 0) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message={`[${response.data.groupName}] saved.`}
                />,
            );

            this.context.history.push(`${AppNavPaths.UserGroups}`);
        }

        this.setSaving(false);
    };

    render() {
        const {
            group,
            originalData,
            errors,
            formValidated,
            userGroups,
            saving,
        } = this.state;

        const groupId = this.props.match.params.id;
        const existing = !!group.id;

        if (groupId && !existing) {
            return <Progress />;
        }

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, group)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="groupForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={
                        !group.id ? 'New Resource Group' : 'Edit Resource Group'
                    }
                    formName="groupForm"
                    formRef={this.formRef}
                    onSubmit={(values) => this.onSubmit(values)}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                    backUrl={AppNavPaths.UserGroups}
                    backLabel="Back to Resource Groups"
                    initialValues={group || {}}
                >
                    {() => (
                        <>
                            <GroupedRow>
                                <FormGroupColumn>
                                    <Input
                                        label="Name"
                                        id="groupName"
                                        name="groupName"
                                        placeholder="Enter Name (max 150 characters)"
                                        maxLength="150"
                                        pattern="[^()/><\][\\\x22,;|]+"
                                        error="Name is required and can only contain the following special characters: hyphens and periods."
                                        required
                                    />

                                    <ValidatedSelect
                                        id="types"
                                        name="userGroups"
                                        label="User Groups"
                                        required
                                        isMulti
                                        asPrimitive
                                        options={userGroups}
                                        error="A group is required."
                                    />
                                </FormGroupColumn>
                            </GroupedRow>

                            <FormDivider />

                            <FlexCenterRow className="mb-3">
                                <SaveButton
                                    label={!existing ? 'Save New Resource Group' : 'Save'}
                                />
                            </FlexCenterRow>
                        </>
                    )}
                </AppPageForm>
            </>
        );
    }
}
export default UserGroupForm;
